import React, { useRef, useState, useEffect } from "react";
import { FaBuilding, FaEye, FaImage, FaLink, FaPlus, FaShareAlt } from "react-icons/fa";
import { MdDescription } from "react-icons/md";
import { useConfirmation } from "../../common/ConfirmationContext";
import { presentationIcon, renderIcon } from "../../common/IconLibrary";
import PresentationConfigurator from "./PresentationConfigurator";
import { useNavigate } from "react-router-dom";

const PresentationEditor = ({ item, onSave, onClose, onDelete }) => {
  const nameInputRef = useRef(null);
  const { openConfirmation } = useConfirmation();
  const [editedItem, setEditedItem] = useState(item);
  const [configuration, setConfiguration] = useState(item?.configuration || []);
  const presentationConfiguratorRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    if (item && item.configuration) {
      setConfiguration(item.configuration);
    }
  }, [item]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, []);

  useEffect(() => {
    setEditedItem((prevItem) => ({
      ...prevItem,
      configuration: configuration,
    }));
  }, [configuration]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "url_name") {
      if (/^[A-Za-z0-9_-]+$/.test(value) || value === "") {
        setEditedItem((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setEditedItem((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSave(editedItem);
  };

  const handlePreview = (e) => {
    e.preventDefault();
    editedItem.previewing = true;
    onSave(editedItem);
    setTimeout(() => {
      navigate(`/presentation/${editedItem.id}?preview=true`);
    }, 500);
  };

  const handleDeleteClick = () => {
    openConfirmation({
      title: "Delete Presentation",
      message:
        "Are you sure you want to delete this presentation? This action cannot be undone.",
      onConfirm: () => {
        onDelete(item.id);
        onClose();
      },
    });
  };

  const handleAddConfiguration = () => {
    if (presentationConfiguratorRef.current) {
      presentationConfiguratorRef.current.addConfig();
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/presentation/${editedItem.url_name || editedItem.id}`;
    navigator.clipboard.writeText(url);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
      <div className="bg-gray-800 p-6 rounded-lg w-full max-w-[80vw] m-4 max-h-[80vh] overflow-y-auto dark-slim-scrollbar">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            <h3 className="text-gray-100 mb-2 font-sol-w01">Presentation</h3>
            <div className="relative">
              <span className="absolute left-2 top-3 text-gray-400">
                {renderIcon(presentationIcon, 18, 18)}
              </span>
              <input
                ref={nameInputRef}
                type="text"
                name="name"
                value={editedItem?.name}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Presentation Name"
              />
            </div>
            <div className="relative">
              <MdDescription className="absolute left-2 top-3 text-gray-400" />
              <textarea
                name="description"
                value={editedItem?.description}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Description"
                rows="3"
              />
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-gray-100 mb-2 font-sol-w01">Whitelabeling</h3>
            <div className="flex gap-4">
              <div className="w-2/3 space-y-3">
                <div className="relative">
                  <FaBuilding className="absolute left-2 top-3 text-gray-400" />
                  <input
                    type="text"
                    name="whitelabel_org_name"
                    value={editedItem?.whitelabel_org_name}
                    onChange={handleChange}
                    className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                    placeholder="Organization Name"
                  />
                </div>
                <div className="relative">
                  <FaImage className="absolute left-2 top-3 text-gray-400" />
                  <input
                    type="text"
                    name="whitelabel_logo_url"
                    value={editedItem?.whitelabel_logo_url}
                    onChange={handleChange}
                    className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                    placeholder="Logo URL"
                  />
                </div>
              </div>
              <div className="w-1/3 flex items-center justify-center rounded">
                {editedItem?.whitelabel_logo_url ? (
                  <img
                    src={editedItem.whitelabel_logo_url}
                    alt=""
                    className="max-w-full object-contain max-h-[90px] bg-white p-4 rounded-xl"
                  />
                ) : (
                  <span className="text-gray-400">Logo Preview</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div className="flex font-semibold text-gray-400 ">
                {window.location.origin}/presentation/
              </div>
              <div className="flex-1 relative">
                <FaLink className="absolute left-2 top-3 text-gray-400" />
                <input
                  type="text"
                  name="url_name"
                  value={editedItem?.url_name}
                  onChange={handleChange}
                  className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                  placeholder="URL Friendly Name"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="ml-[-12px] mb-[-18px]">
          <button
            onClick={handleAddConfiguration}
            className="bg-gray-700 text-white p-2 rounded-full hover:bg-blue-500"
            title="Add Configuration"
          >
            <FaPlus />
          </button>
        </div>
        <div className="mb-4 h-[450px] overflow-y-auto dark-slim-scrollbar">
          <PresentationConfigurator
            ref={presentationConfiguratorRef}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
        </div>
        <div className="flex justify-between">
          <div>
            {editedItem && (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-500"
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex space-x-4">
            <div className="relative group">
              <button
                type="button"
                className="bg-green-600 text-white p-2 rounded-full hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handlePreview}
                aria-label="Preview"
              >
                <FaEye className="w-5 h-5" />
              </button>
              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Preview
              </span>
            </div>
            <div className="relative group">
              <button
                type="button"
                className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
                onClick={handleShare}
                aria-label="Share presentation link"
              >
                <FaShareAlt className="w-5 h-5" />
              </button>
              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Share presentation link
              </span>
            </div>
          </div>
          <div>
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded mr-2 hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresentationEditor;
